/**
 * API functions for collection richlist
 */

import { apiClient } from './client';

/**
 * Holder entry for collections WITHOUT points system
 */
export interface SimpleHolderEntry {
  rank: number;
  owner_address: string;
  nft_count: number;
}

/**
 * Holder entry for collections WITH points system
 */
export interface PointsHolderEntry {
  rank: number;
  owner_address: string;
  nft_count: number;
  total_points: number;
  /** NFT counts by point tier, e.g., { "0.5": 10, "1": 5, "2": 3 } - supports decimal tiers */
  tiers: Record<string, number>;
}

/**
 * Richlist response for collections WITHOUT points
 */
export interface SimpleRichlistResult {
  has_points_system: false;
  holders: SimpleHolderEntry[];
  total_holders: number;
}

/**
 * Richlist response for collections WITH points
 */
export interface PointsRichlistResult {
  has_points_system: true;
  /** Sorted array of distinct point values in this collection */
  point_tiers: number[];
  holders: PointsHolderEntry[];
  total_holders: number;
  total_points: number;
  /** Optional strategy identifier for custom column labels */
  strategy?: string | null;
}

export type RichlistResult = SimpleRichlistResult | PointsRichlistResult;

/**
 * Fetch richlist for a collection
 */
export async function getCollectionRichlist(collectionId: string): Promise<RichlistResult> {
  const response = await apiClient.get<{ success: boolean; data: RichlistResult }>(
    `/collections/${collectionId}/richlist`
  );
  return response.data;
}

/**
 * One entry in the point-distribution chart: how many NFTs sit at a given
 * point value, and the rank range they cover.
 */
export interface PointDistributionEntry {
  points: number;
  nft_count: number;
  min_rank: number | null;
  max_rank: number | null;
}

export interface PointsDistributionResult {
  has_points_system: boolean;
  total_nfts: number;
  strategy: string | null;
  distribution: PointDistributionEntry[];
}

/**
 * Fetch the points-per-tier distribution for a collection. Cheap aggregation;
 * safe to call even when the richlist tab hasn't been opened.
 */
export async function getCollectionPointsDistribution(
  collectionId: string
): Promise<PointsDistributionResult> {
  const response = await apiClient.get<{ success: boolean; data: PointsDistributionResult }>(
    `/collections/${collectionId}/points-distribution`
  );
  return response.data;
}
